import { useEffect, useRef } from 'react';
import { useMobileGameLayout } from './useMobileGameLayout';

const key = '__boardGameModal';
type Entry = { id: number; close: () => void; active: boolean };
const entries: Entry[] = [];
const waiting: Entry[] = [];
let sequence = 0;
let pending = false;
let modalUrl = '';
let pendingNavigation: string | undefined;

function syncHistory() {
  if (pending) return;
  // Explicit application navigation owns its new history entry.
  if (entries.length && (window.location.href !== modalUrl || window.history.state?.[key] !== entries[entries.length - 1].id)) {
    entries.splice(0);
  }
  let removed = 0;
  for (let i = entries.length - 1; i >= 0 && !entries[i].active; i--) removed++;
  if (removed) {
    pending = true;
    window.history.go(-removed);
    return;
  }
  if (pendingNavigation !== undefined) {
    window.history.pushState({}, '', pendingNavigation);
    pendingNavigation = undefined;
  }
  for (const entry of waiting.splice(0)) {
    if (!entry.active) continue;
    // A reload can retain a marker whose modal belonged to the old document.
    if (!entries.length && window.history.state?.[key] !== undefined) {
      const state = { ...window.history.state };
      delete state[key];
      window.history.replaceState(state, '');
    }
    modalUrl = window.location.href;
    entries.push(entry);
    window.history.pushState({ ...window.history.state, [key]: entry.id }, '', modalUrl);
  }
}

// Capture before the application's route listener: modal traversals are not routes.
window.addEventListener('popstate', event => {
  const target = event.state?.[key];
  if (!entries.length) return;
  const index = entries.findIndex(entry => entry.id === target);
  const samePage = window.location.href === modalUrl;
  const dismissed = entries.splice(index + 1);
  pending = false;
  if (samePage) event.stopImmediatePropagation();
  for (const entry of dismissed.reverse()) {
    if (entry.active) { entry.active = false; entry.close(); }
  }
  syncHistory();
}, true);

/** Remove modal steps before the application pushes a new page. */
export function pushPageHistory(url: string) {
  for (const entry of [...entries, ...waiting].reverse()) {
    if (entry.active) { entry.active = false; entry.close(); }
  }
  pendingNavigation = url;
  syncHistory();
}

/** One same-URL history entry per mobile modal, shared across portal roots. */
export function useModalHistory(open: boolean, onClose: () => void) {
  const mobile = useMobileGameLayout();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || !mobile) return;
    const entry: Entry = { id: ++sequence, active: true, close: () => close.current() };
    waiting.push(entry);
    // Batch nested mounts, StrictMode cleanup, and dialog replacements.
    queueMicrotask(syncHistory);
    return () => { entry.active = false; queueMicrotask(syncHistory); };
  }, [open, mobile]);
}
